import { AnchorRequestId, BookmarkId, CameraSubjectRequestId, CameraSubjectResponseId, CapabilitiesId, FeedbackId, GPSRequestId, RoomOutcomeReportId, ScanningRequestId, StreemshotId, StreemshotRequestId, } from '@streem/domain-id';
import { invariant } from '@streem/toolbox';
import { CameraSubjectResponseStatus, Long, Timestamp, WallItem, } from 'streem-sdk-protobuf';
let heartbeatCount = 0;
export class WallItemBuilder {
    constructor(ownerDeviceId, ownerUserId) {
        this.ownerDeviceId = ownerDeviceId;
        this.ownerUserId = ownerUserId;
    }
    buildStreemshotRequestItem(requestedOfUserId, requestedOfDeviceId) {
        const id = new StreemshotRequestId().toString();
        const streemshotId = new StreemshotId().toString();
        return this.buildWallItem({
            id,
            type: 'streemshotRequest',
            streemshotRequest: {
                streemshotId,
                requestedOfUserId: requestedOfUserId.toString(),
                requestedOfDeviceId: requestedOfDeviceId.toString(),
            },
        });
    }
    buildStreemshotResponseItem(streemshotId, streemshotPayload) {
        return this.buildWallItem({
            id: streemshotId.toString(),
            type: 'streemshot',
            streemshot: new WallItem.Streemshot(streemshotPayload),
        });
    }
    buildStreemshotRevisionItem(streemshotId, streemshotPayload) {
        return this.buildWallItem({
            id: streemshotId.toString(),
            type: 'streemshotRevision',
            streemshotRevision: new WallItem.StreemshotRevision(streemshotPayload),
        });
    }
    buildGeolocationRequestWallItem(requestedOfUserId, requestedOfDeviceId) {
        const id = new GPSRequestId().toString();
        return this.buildWallItem({
            id,
            type: 'artifactRequest',
            artifactRequest: {
                artifactId: id,
                requestedForUserId: requestedOfUserId.toString(),
                requestedForDeviceId: requestedOfDeviceId.toString(),
                artifactType: WallItem.ArtifactType.GPS_POSITION,
            },
        });
    }
    buildGeolocationResponseItem(gpsId, gpsPayLoad) {
        return this.buildWallItem({
            id: gpsId.toString(),
            type: 'artifact',
            artifact: new WallItem.Artifact(gpsPayLoad),
        });
    }
    buildBookmarkWallItem(bookmarkPayload, id) {
        const bookmarkId = id || new BookmarkId().toString();
        return this.buildWallItem({
            id: bookmarkId,
            type: 'artifact',
            artifact: new WallItem.Artifact(bookmarkPayload),
        });
    }
    buildRoomOutcomeReportWallItem(roomOutcomeReport, id) {
        const roomOutcomeReportId = id || new RoomOutcomeReportId().toString();
        return this.buildWallItem({
            id: roomOutcomeReportId,
            type: 'artifact',
            artifact: new WallItem.Artifact(roomOutcomeReport),
        });
    }
    buildRoomWallItem(room) {
        return this.buildWallItem({ room, id: 'room', type: 'room' });
    }
    buildUserWallItem(id, user) {
        return this.buildWallItem({ user, id: id.toString(), type: 'user' });
    }
    buildTrackWallItem(type, track) {
        invariant(this.ownerUserId, 'Expected ownerUserId to be defined when building track wall item');
        return this.buildWallItem({
            [type]: track,
            id: this.getTrackId(type, this.ownerUserId),
            type,
        });
    }
    buildHeartbeatWallItem() {
        return this.buildWallItem({
            id: `heartbeat:${this.ownerUserId}`,
            type: 'heartbeat',
            heartbeat: {
                timestamp: this.getProtobufTimestamp(),
                count: ++heartbeatCount,
            },
        });
    }
    buildARScanningRequest(arScanningRequest) {
        const id = new ScanningRequestId().toString();
        return this.buildWallItem({
            id,
            type: 'arScanningRequest',
            arScanningRequest,
        });
    }
    buildARAnchorRequest(arAnchorRequest) {
        const id = new AnchorRequestId().toString();
        return this.buildWallItem({
            id,
            type: 'arAnchorRequest',
            arAnchorRequest,
        });
    }
    buildCatalogItem(id, catalogItem) {
        return this.buildWallItem({
            id: id.toString(),
            type: 'catalogItem',
            catalogItem,
        });
    }
    buildElementGroup(id, elementGroup) {
        return this.buildWallItem({
            id: id.toString(),
            type: 'elementGroup',
            elementGroup,
        });
    }
    buildCapabilities(capabilities) {
        // Capabilities specific to device - use deviceId to create reproducible IDs
        const id = new CapabilitiesId(this.ownerDeviceId.toUUID()).toString();
        return this.buildWallItem({
            id,
            type: 'capabilities',
            capabilities,
        });
    }
    buildCameraSubjectRequestWallItem(requestedForUserId, requestedForDeviceId, requestedCameraSubject) {
        const id = new CameraSubjectRequestId().toString();
        return this.buildWallItem({
            id: id,
            type: 'cameraSubjectRequest',
            cameraSubjectRequest: {
                requestedForUserId: requestedForUserId.toString(),
                requestedForDeviceId: requestedForDeviceId.toString(),
                requestedCameraSubject: requestedCameraSubject,
            },
        });
    }
    buildCameraSubjectResponseWallItem(inResponseToWallItemId, accepted, status) {
        const id = new CameraSubjectResponseId().toString();
        // when status is not set, infer its value based on accepted.  Only legacy versions of
        //    the mobile apps will omit status and they do not implement abort.
        const cameraResponseStatus = status
            ? status
            : accepted
                ? CameraSubjectResponseStatus.CAMERA_SUBJECT_RESPONSE_STATUS_ACCEPTED
                : CameraSubjectResponseStatus.CAMERA_SUBJECT_RESPONSE_STATUS_REJECTED;
        return this.buildWallItem({
            id: id,
            type: 'cameraSubjectResponse',
            cameraSubjectResponse: {
                accepted: accepted,
                inResponseToWallItemId: inResponseToWallItemId,
                status: cameraResponseStatus,
            },
        });
    }
    buildCreateDetectionWallItem(streemshotId, description, type) {
        const id = new FeedbackId().toString();
        return this.buildWallItem({
            id,
            type: 'userDetectionFeedback',
            userDetectionFeedback: {
                streemshotId: streemshotId.toString(),
                submittedByUserId: this.ownerUserId?.toString(),
                createDetection: {
                    description,
                    type,
                },
            },
        });
    }
    buildUpdateDetectionWallItem(streemshotId, detectionId, description) {
        const id = new FeedbackId().toString();
        return this.buildWallItem({
            id,
            type: 'userDetectionFeedback',
            userDetectionFeedback: {
                streemshotId: streemshotId.toString(),
                submittedByUserId: this.ownerUserId?.toString(),
                updateDetection: {
                    detectionId: detectionId.toString(),
                    description,
                },
            },
        });
    }
    buildHideDetectionWallItem(streemshotId, detectionId) {
        const id = new FeedbackId().toString();
        return this.buildWallItem({
            id,
            type: 'userDetectionFeedback',
            userDetectionFeedback: {
                streemshotId: streemshotId.toString(),
                submittedByUserId: this.ownerUserId?.toString(),
                hideDetection: {
                    detectionId: detectionId.toString(),
                },
            },
        });
    }
    buildWallItem(itemData) {
        invariant(this.ownerUserId, 'Expected ownerUserId to be defined when building wall item');
        return new WallItem({
            ...itemData,
            ownerUserId: this.ownerUserId.toString(),
            ownerDeviceId: this.ownerDeviceId.toString(),
            clientCreatedAt: this.getProtobufTimestamp(),
        });
    }
    getProtobufTimestamp(timestamp) {
        let date;
        if (!timestamp) {
            date = new Date();
        }
        else if (typeof date === 'string') {
            date = new Date(timestamp);
        }
        else {
            date = timestamp;
        }
        const millis = date.getTime();
        return new Timestamp({
            seconds: new Long(millis / 1000),
            nanos: (millis % 1000) * 1e6,
        });
    }
    getTrackId(type, ownerUserId) {
        return `${type}${ownerUserId}`;
    }
}
//# sourceMappingURL=wall_item_builder.js.map